// components/expenses/ExpensesTable.jsx
import React, { useState } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Tooltip,
    Typography,
    Chip,
    CircularProgress,
    Stack
} from '@mui/material';
import {
    Edit,
    Delete,
    CheckCircleOutline,
    CancelOutlined,
    PaymentsOutlined,
    History as HistoryIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import ExpenseAuditDialog from './ExpenseAuditDialog';

const ExpensesTable = ({
    expenses,
    loading,
    user,
    onEdit,
    onDelete,
    onApprove,
    onReject,
    onMarkAsPaid,
    page,
    rowsPerPage,
    total,
    onPageChange,
    onRowsPerPageChange,
    filters
}) => {
    const { t } = useTranslation();
    const [auditExpense, setAuditExpense] = useState(null);
    const [auditDialogOpen, setAuditDialogOpen] = useState(false);

    const handleOpenAudit = (expense) => {
        setAuditExpense(expense);
        setAuditDialogOpen(true);
    };

    const handleCloseAudit = () => {
        setAuditDialogOpen(false);
        setAuditExpense(null);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'success';
            case 'rejected': return 'error';
            case 'paid': return 'info';
            case 'pending':
            default: return 'warning';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'approved': return t('expenses.approved', 'Approved');
            case 'rejected': return t('expenses.rejected', 'Rejected');
            case 'paid': return t('expenses.paid', 'Paid');
            case 'pending':
            default: return t('expenses.pending', 'Pending');
        }
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return format(new Date(date), 'dd/MM/yyyy');
    };

    const getEmptyMessage = () => {
        if (filters?.statuses?.length > 0) {
            return t('expenses.noStatusResults', 'No expenses found with selected statuses');
        }
        if (filters?.categoryIds?.length > 0) {
            return t('expenses.noCategoryResults', 'No expenses found in selected categories');
        }
        if (filters?.from || filters?.to) {
            return t('expenses.noDateRangeResults', 'No expenses found in this date range');
        }
        return t('expenses.noData', 'No expenses found');
    };

    return (
        <>
            <Paper sx={{ width: '100%' }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('expenses.description', 'Description')}</TableCell>
                                <TableCell align="right">{t('expenses.amount', 'Amount')}</TableCell>
                                <TableCell>{t('common.category', 'Category')}</TableCell>
                                <TableCell>{t('expenses.date', 'Date')}</TableCell>
                                <TableCell>{t('common.branch', 'Branch')}</TableCell>
                                <TableCell>{t('common.user', 'User')}</TableCell>
                                <TableCell align="center">{t('expenses.status', 'Status')}</TableCell>
                                <TableCell>{t('common.actions', 'Actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : expenses.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">
                                            {getEmptyMessage()}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                expenses.map((expense) => (
                                    <TableRow
                                        key={expense._id}
                                        hover
                                        sx={{
                                            backgroundColor: expense.status === 'rejected'
                                                ? 'rgba(244, 67, 54, 0.04)'
                                                : expense.status === 'paid'
                                                    ? 'rgba(33, 150, 243, 0.04)'
                                                    : 'inherit'
                                        }}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {expense.name}
                                            </Typography>
                                            {expense.notes && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {expense.notes}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="bold">
                                                {expense.amount?.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {expense.category?.name ? (
                                                <Chip
                                                    label={expense.category.name}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                        <TableCell>{formatDate(expense.date)}</TableCell>
                                        <TableCell>{expense.branch?.name || '-'}</TableCell>
                                        <TableCell>{expense.createdBy?.username || expense.createdBy?.name || '-'}</TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={getStatusLabel(expense.status)}
                                                color={getStatusColor(expense.status)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5}>
                                                {/* Audit Trail Button */}
                                                <Tooltip title={t('expenses.audit.viewHistory', 'View History')}>
                                                    <IconButton
                                                        size="small"
                                                        color="default"
                                                        onClick={() => handleOpenAudit(expense)}
                                                    >
                                                        <HistoryIcon />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title={t('common.edit', 'Edit')}>
                                                    <IconButton size="small" onClick={() => onEdit(expense)}>
                                                        <Edit />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('common.delete', 'Delete')}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onDelete(expense)}
                                                    >
                                                        <Delete color="error" />
                                                    </IconButton>
                                                </Tooltip>
                                                {user?.isSuperAdmin && expense.status === 'pending' && (
                                                    <>
                                                        <Tooltip title={t('expenses.approve', 'Approve')}>
                                                            <IconButton
                                                                size="small"
                                                                color="success"
                                                                onClick={() => onApprove(expense)}
                                                            >
                                                                <CheckCircleOutline />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={t('expenses.reject', 'Reject')}>
                                                            <IconButton
                                                                size="small"
                                                                color="warning"
                                                                onClick={() => onReject(expense)}
                                                            >
                                                                <CancelOutlined />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}
                                                {user?.isSuperAdmin && expense.status === 'approved' && (
                                                    <Tooltip title={t('expenses.markAsPaid', 'Mark as Paid')}>
                                                        <IconButton
                                                            size="small"
                                                            color="info"
                                                            onClick={() => onMarkAsPaid(expense)}
                                                        >
                                                            <PaymentsOutlined />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Paper sx={{ width: '100%', mt: 2 }}>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={onPageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={onRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage={t('common.rowsPerPage', 'Rows per page')}
                />
            </Paper>

            {/* Audit Dialog */}
            <ExpenseAuditDialog
                open={auditDialogOpen}
                onClose={handleCloseAudit}
                expense={auditExpense}
            />
        </>
    );
};

export default ExpensesTable;